import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc
} from "firebase/firestore";

import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";

const CategoryManager = ({ open, handleClose }) => {
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!currentUser) return;

      try {
        const categoriesRef = collection(db, `users/${currentUser.uid}/categories`);
        const snapshot = await getDocs(categoriesRef);
        const categoriesList = snapshot.docs.map(currentDoc => ({
          id: currentDoc.id,
          ...currentDoc.data()
        }));
        setCategories(categoriesList);
      } catch (error) {
        enqueueSnackbar(t("catalog.categories.notifications.fetchError") + error.message, {
          variant: "error"
        });
      }
    };

    if (open) {
      fetchCategories();
    }
  }, [currentUser, enqueueSnackbar, open, t]);

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;

    try {
      const categoriesRef = collection(db, `users/${currentUser.uid}/categories`);
      const docRef = await addDoc(categoriesRef, { name: newCategory.trim() });
      setCategories(prev => [...prev, { id: docRef.id, name: newCategory.trim() }]);
      setNewCategory("");
      enqueueSnackbar(t("catalog.categories.notifications.addSuccess"), {
        variant: "success"
      });
    } catch (error) {
      enqueueSnackbar(t("catalog.categories.notifications.addError") + error.message, {
        variant: "error"
      });
    }
  };

  const handleEditCategory = async (id, name) => {
    try {
      const categoryDoc = doc(db, `users/${currentUser.uid}/categories`, id);
      await updateDoc(categoryDoc, { name });
      setCategories(prev => prev.map(category => (category.id === id ? { ...category, name } : category)));
      setEditingCategory(null);
      enqueueSnackbar(t("catalog.categories.notifications.updateSuccess"), {
        variant: "success"
      });
    } catch (error) {
      enqueueSnackbar(t("catalog.categories.notifications.updateError") + error.message, {
        variant: "error"
      });
    }
  };

  const handleDeleteCategory = async () => {
    try {
      const categoryDoc = doc(db, `users/${currentUser.uid}/categories`, categoryToDelete);
      await deleteDoc(categoryDoc);
      setCategories(prev => prev.filter(category => category.id !== categoryToDelete));
      enqueueSnackbar(t("catalog.categories.notifications.deleteSuccess"), {
        variant: "success"
      });
    } catch (error) {
      enqueueSnackbar(t("catalog.categories.notifications.deleteError") + error.message, {
        variant: "error"
      });
    } finally {
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle
          sx={{
            px: 4,
            py: 2,
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t("catalog.categories.title")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ backgroundColor: "action.hover" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2.5, md: 4 }, py: 3 }}>
          <Stack spacing={2.5}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: theme =>
                  theme.palette.mode === "dark"
                    ? "rgba(148, 163, 184, 0.12)"
                    : "rgba(15, 23, 42, 0.03)"
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.25 }}>
                {t("catalog.categories.addNew")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <TextField
                  label={t("catalog.categories.name")}
                  value={newCategory}
                  onChange={event => setNewCategory(event.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handleAddCategory}
                  startIcon={<AddIcon />}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    backgroundColor: theme =>
                      theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.22)" : "#1f2937",
                    color: theme => (theme.palette.mode === "dark" ? "#e2e8f0" : "#f8fafc"),
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: theme =>
                        theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.3)" : "#111827",
                      boxShadow: "none"
                    }
                  }}
                >
                  {t("common.add")}
                </Button>
              </Stack>
            </Paper>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, py: 1.8 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.8 }}>{t("catalog.categories.type")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.8 }}>{t("common.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category, index) => (
                    <TableRow key={category.id}>
                      <TableCell sx={{ py: 1.25 }}>{index + 1}</TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {editingCategory === category.id ? (
                          <TextField
                            value={category.name}
                            onChange={event => {
                              setCategories(prev =>
                                prev.map(cat =>
                                  cat.id === category.id
                                    ? { ...cat, name: event.target.value }
                                    : cat
                                )
                              );
                            }}
                            fullWidth
                            size="small"
                          />
                        ) : (
                          <Typography sx={{ fontWeight: 500 }}>{category.name}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {editingCategory === category.id ? (
                          <Stack direction="row" spacing={1}>
                            <Tooltip title={t("common.save")}>
                              <IconButton
                                onClick={() => handleEditCategory(category.id, category.name)}
                                size="small"
                                color="primary"
                              >
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Button onClick={() => setEditingCategory(null)} color="inherit" size="small">
                              {t("common.cancel")}
                            </Button>
                          </Stack>
                        ) : (
                          <Stack direction="row" spacing={1}>
                            <Tooltip title={t("common.edit")}>
                              <IconButton onClick={() => setEditingCategory(category.id)} size="small">
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("common.delete")}>
                              <IconButton
                                onClick={() => {
                                  setCategoryToDelete(category.id);
                                  setDeleteDialogOpen(true);
                                }}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {categories.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ py: 5 }}>
                        <Box textAlign="center">
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t("catalog.categories.title")}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Ajoute une première catégorie pour organiser ton catalogue.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 4,
            py: 2,
            borderTop: theme => `1px solid ${theme.palette.divider}`
          }}
        >
          <Button onClick={handleClose} color="inherit" sx={{ textTransform: "none" }}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("catalog.categories.deleteConfirmation.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("catalog.categories.deleteConfirmation.message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleDeleteCategory} color="error">
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CategoryManager;
